import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getDemoPersonaId, getDemoPersonas, supabase } from "@/lovable-demo/runtime/client";
import { recordAuditEvent } from "@/lovable-demo/lib/governance/audit";
import {
  captureEvidenceSnapshot,
  markEvidenceNotRecorded,
  registerEvidence,
} from "@/lovable-demo/lib/governance/evidence";

import { logLineage } from "@/lovable-demo/lib/governance/lineage";
import { evaluateAuthority, type AuthorityRule } from "@/lovable-demo/lib/governance/authority";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import {
  buildEvidenceItem,
  validateActor,
  validateAttachment,
  type EvidenceActor,
  type EvidenceAttachment,
} from "./evidence-attach";
import {
  submitPfmeaProposal,
  submitPfmeaSignoff,
  type PfmeaChangeRequest,
  type PfmeaSignoff,
  type Rpc,
  type SignoffRole,
} from "./pfmea-signoff";
import { getPolicyValue } from "@/lovable-demo/lib/governance/policy";
import { submitVerdictChange, type Verdict, type VerdictEvidence } from "./compliance-verdict";
import { setActionPriorityTable } from "./action-priority";
import {
  submitDefaultCarryFactor,
  submitRecordCarryFactor,
  type CarryFactorRpc,
} from "./carry-factor";
import {
  DEFAULT_EIGHTD_MAPPING,
  dGate,
  normalizeMapping,
  stageForD,
  stageGate,
  validateDDueDate,
  type EightDMapping,
} from "./stage-link";
import { buildSeedData, DATA_VERSION } from "./seed";
import { buildAllClaims } from "./claims";
import { SEED_SUPPLIERS } from "./suppliers";
import type {
  Claim,
  DecisionVerdict,
  EvidenceItem,
  PfmeaAfterField,
  Program,
  ProblemRecord,
  Recommendation,
  SeriesPoint,
  Supplier,
  WarrantyDataSet,
} from "./types";

/**
 * Result of a controlled governance action (Class A). `ok` is only true once the
 * governed record, its audit entry and — for decisions — the Evidence Snapshot
 * at Decision have all been confirmed.
 */
export type GovernanceResult = { ok: true } | { ok: false; error: string };

const GOVERNANCE_FAILED =
  "This action was not recorded — the governance audit trail could not be written. Nothing has been saved. Please try again.";
const SAVE_FAILED =
  "This action was not recorded — the governed record could not be saved. Please try again.";
const NO_CHANGE = "Nothing was recorded — this action is not available for this stage.";

interface WarrantyContextValue {
  data: WarrantyDataSet;
  suppliers: Supplier[];
  claims: Claim[];
  hydrated: boolean;
  refreshRecord: (recordId: string) => Promise<void>;
  activeProgramId: string | null;
  setActiveProgram: (id: string | null) => void;
  completeStage: (recordId: string, stageId: string) => Promise<GovernanceResult>;
  addStageEvidence: (
    recordId: string,
    stageId: string,
    name: string,
    attachment: EvidenceAttachment,
  ) => Promise<GovernanceResult>;
  addStageNote: (recordId: string, stageId: string, text: string) => Promise<GovernanceResult>;
  completeEightD: (recordId: string, dId: string) => Promise<GovernanceResult>;
  addEightDEvidence: (
    recordId: string,
    dId: string,
    name: string,
    attachment: EvidenceAttachment,
  ) => Promise<GovernanceResult>;
  /** Creates a pending change request; never changes the rating itself. */
  proposePfmeaChange: (
    recordId: string,
    rowId: string,
    field: PfmeaAfterField,
    newValue: number,
    rationale: string,
    openRequest: PfmeaChangeRequest | undefined,
  ) => Promise<GovernanceResult>;
  signOffPfmea: (
    recordId: string,
    request: PfmeaChangeRequest,
    signoffs: PfmeaSignoff[],
    decision: "approve" | "reject",
    role: SignoffRole,
    comment: string,
  ) => Promise<GovernanceResult>;
  /** Package #2.7 — governed compliance verdict change (Class A). */
  changeClauseVerdict: (
    recordId: string,
    clauseId: string,
    newVerdict: Verdict,
    rationale: string,
    evidence: VerdictEvidence | null,
  ) => Promise<GovernanceResult>;
  changeRecordCarryFactor: (
    recordId: string,
    newValue: number,
    rationale: string,
  ) => Promise<GovernanceResult>;
  changeDefaultCarryFactor: (newValue: number, rationale: string) => Promise<GovernanceResult>;
  defaultCarryFactor: number;
  decideRecommendation: (
    recordId: string,
    rec: Recommendation,
    verdict: DecisionVerdict,
    finalAction: string,
    note: string,
  ) => Promise<GovernanceResult>;
  /** Package #2.4 — stage ↔ 8D link (policy workflow.eightd_mapping). */
  eightDMapping: EightDMapping;
  setEightDDueDate: (recordId: string, dId: string, date: string) => Promise<GovernanceResult>;
  /** Adds a record created through a confirmed governed decision to the view. */
  adoptRecord: (record: ProblemRecord) => void;
}

const WarrantyContext = createContext<WarrantyContextValue | null>(null);

const nowIso = () => new Date().toISOString();
const uid = () => Math.random().toString(36).slice(2, 10);

/** Signed-in person's display name and job title (from their profile). */
async function evidenceActor(): Promise<EvidenceActor | null> {
  const { data } = await supabase.auth.getUser();
  const user = data.user;
  if (!user) return null;
  const { data: profile } = await supabase
    .from("profiles")
    .select("full_name, job_title")
    .eq("id", user.id)
    .maybeSingle();
  const meta = (user.user_metadata ?? {}) as { full_name?: string };
  return {
    name: profile?.full_name || meta.full_name || user.email || "",
    title: profile?.job_title || "",
  };
}

const rpc: Rpc = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  return { data, error: error ? { message: error.message } : null };
};

const carryRpc: CarryFactorRpc = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn, args as never);
  return { data, error: error ? { message: error.message } : null };
};

async function loadAuthority(): Promise<{ roles: AppRole[]; rules: AuthorityRule[] }> {
  const { fetchMyRoles } = await import("@/lovable-demo/lib/governance/roles");
  const [roles, rules] = await Promise.all([
    fetchMyRoles(),
    supabase.from("decision_authority_rules").select("*").eq("active", true),
  ]);
  if (rules.error) throw new Error(rules.error.message);
  return { roles, rules: (rules.data ?? []) as unknown as AuthorityRule[] };
}

/** Prepares a real evidence item, or returns an error. Nothing is written here. */
async function prepareEvidence(
  name: string,
  attachment: EvidenceAttachment,
): Promise<{ item: EvidenceItem } | { error: string }> {
  const invalid = validateAttachment(attachment);
  if (invalid) return { error: invalid };
  const actor = await evidenceActor();
  const noActor = validateActor(actor);
  if (noActor || !actor) return { error: noActor ?? "Not signed in." };
  return { item: buildEvidenceItem(name, attachment, actor, nowIso(), `ev-${uid()}`) };
}

/** Shape written to the problem_records table. */
function recordRow(r: ProblemRecord) {
  return {
    id: r.id,
    code: r.code,
    title: r.title,
    program_id: r.programId,
    risk: r.risk,
    status: r.status,
    risk_score: r.riskScore,
    exposure: r.financial.exposure,
    record: JSON.parse(JSON.stringify(r)) as never,
    updated_at: nowIso(),
  };
}

function programFromRow(row: {
  id: string;
  code: string;
  name: string;
  vehicle_line: string;
  model_year: number;
  units_in_service: number;
  region: string;
  c1000: number;
  c1000_target: number;
  trend: unknown;
}): Program {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    vehicleLine: row.vehicle_line,
    modelYear: row.model_year,
    unitsInService: row.units_in_service,
    region: row.region,
    c1000: Number(row.c1000),
    c1000Target: Number(row.c1000_target),
    trend: (row.trend as SeriesPoint[]) ?? [],
  };
}

export function WarrantyProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<WarrantyDataSet>(() => buildSeedData());
  const [hydrated, setHydrated] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [activeProgramId, setActiveProgramId] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>(SEED_SUPPLIERS);
  const [claims, setClaims] = useState<Claim[]>(() => {
    const seed = buildSeedData();
    return buildAllClaims(seed.records, seed.programs);
  });
  const [eightDMapping, setEightDMapping] = useState<EightDMapping>(DEFAULT_EIGHTD_MAPPING);
  const [defaultCarryFactor, setDefaultCarryFactor] = useState(0.08);
  const dataRef = useRef(data);
  dataRef.current = data;

  const loadFromDb = useCallback(async () => {
    const [programs, records, supplierRows, claimRows] = await Promise.all([
      supabase.from("programs").select("*").order("code"),
      supabase.from("problem_records").select("*").order("code"),
      supabase.from("suppliers").select("*").order("name"),
      supabase.from("claims").select("*").order("repair_date", { ascending: false }),
    ]);
    if (claimRows.data) {
      setClaims(
        claimRows.data.map((row) => ({
          id: row.id,
          claimNumber: row.claim_number,
          vin: row.vin,
          recordId: row.record_id ?? "",
          programId: row.program_id ?? "",
          vehicleLine: row.vehicle_line,
          modelYear: row.model_year,
          failureDate: row.failure_date,
          reportedDate: row.reported_date,
          repairDate: row.repair_date,
          mileage: row.mileage,
          description: row.description,
          component: row.component,
          failureCode: row.failure_code,
          dealer: row.dealer,
          region: row.region,
          laborCost: Number(row.labor_cost),
          partsCost: Number(row.parts_cost),
          totalCost: Number(row.total_cost),
          status: row.status as Claim["status"],
          laborHours: row.labor_hours == null ? undefined : Number(row.labor_hours),
          laborRate: row.labor_rate == null ? undefined : Number(row.labor_rate),
          partNumber: row.part_number ?? undefined,
          partQuantity: row.part_quantity ?? undefined,
          repairCode: row.repair_code ?? undefined,
          causalCode: row.causal_code ?? undefined,
          narrative: row.narrative ?? undefined,
          misMonths: row.mis_months ?? undefined,
          repeatRepair: row.repeat_repair,
          returnedPartStatus: row.returned_part_status ?? undefined,
          returnedPartFindings: row.returned_part_findings ?? undefined,
          productionLot: row.production_lot ?? undefined,
          buildPlant: row.build_plant ?? undefined,
          supplierName: row.supplier_name ?? undefined,
          signalId: row.signal_id ?? undefined,
        })),
      );
    }
    if (supplierRows.data) {
      setSuppliers(
        supplierRows.data.map((row) => ({
          id: row.id,
          name: row.name,
          tier: row.tier,
          category: row.category,
          contactName: row.contact_name,
          contactTitle: row.contact_title,
          email: row.email,
          phone: row.phone,
          address: row.address,
          city: row.city,
          country: row.country,
          qualityRating: Number(row.quality_rating),
          ppm: Number(row.ppm),
          sqeOwner: row.sqe_owner,
          contractRef: row.contract_ref,
          escalationContact: row.escalation_contact,
        })),
      );
    }
    if (
      programs.error ||
      records.error ||
      supplierRows.error ||
      claimRows.error ||
      !programs.data ||
      !records.data
    ) {
      setLoadError(true);
      return false;
    }
    setData({
      version: DATA_VERSION,
      generatedAt: nowIso(),
      programs: programs.data.map((row) => programFromRow(row as never)),
      records: records.data.map((row) => row.record as unknown as ProblemRecord),
    });
    setLoadError(false);
    return true;
  }, []);

  useEffect(() => {
    void loadFromDb()
      .catch(() => setLoadError(true))
      .finally(() => setHydrated(true));
    void getPolicyValue<unknown>("pfmea.action_priority_table", null).then(setActionPriorityTable);
    void getPolicyValue<unknown>("workflow.eightd_mapping", null).then((v) =>
      setEightDMapping(normalizeMapping(v)),
    );
    void getPolicyValue<number>("financial.delay_carry_factor_default", 0.08).then((v) =>
      setDefaultCarryFactor(Number(v)),
    );
    // Demo data is readable to signed-in users only, so reload once a session
    // exists (the provider is mounted at the root, above the sign-in screen).
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "INITIAL_SESSION")
        void loadFromDb().catch(() => setLoadError(true));
    });
    return () => sub.subscription.unsubscribe();
  }, [loadFromDb]);

  /**
   * Controlled governance commit (Class A).
   *
   * Order: visible update → confirmed record save → confirmed governance writes.
   * If either confirmed write fails, the visible change is rolled back, the
   * stored record is restored, and the caller is told the action was NOT
   * recorded. Nothing is ever silently represented as governed.
   */
  const commitRecord = useCallback(
    async (
      recordId: string,
      fn: (r: ProblemRecord) => ProblemRecord,
      /** Confirmed governance writes. Throwing here fails the whole action. */
      governance?: (prev: ProblemRecord, next: ProblemRecord) => Promise<void>,
    ): Promise<GovernanceResult> => {
      const previous = dataRef.current.records.find((r) => r.id === recordId);
      if (!previous) return { ok: false, error: NO_CHANGE };
      const next = fn(previous);
      if (next === previous) return { ok: false, error: NO_CHANGE };
      const actor = getDemoPersonas().find((persona) => persona.id === getDemoPersonaId());
      if (!actor) return { ok: false, error: "Choose an active simulated persona." };
      next.audit = next.audit.map((entry, index) =>
        index < previous.audit.length ? entry : { ...entry, actor: actor.name },
      );

      const show = (rec: ProblemRecord) =>
        setData((prev) => ({
          ...prev,
          records: prev.records.map((r) => (r.id === recordId ? rec : r)),
        }));

      show(next);

      const saved = await supabase.from("problem_records").upsert(recordRow(next));
      if (saved.error) {
        show(previous);
        return { ok: false, error: saved.error.message || SAVE_FAILED };
      }

      if (governance) {
        try {
          await governance(previous, next);
        } catch {
          show(previous);
          await supabase
            .from("problem_records")
            .upsert(recordRow(previous))
            .then(() => undefined);
          return { ok: false, error: GOVERNANCE_FAILED };
        }
      }

      // Class B telemetry: informational only, never blocks or fails the action.
      logLineage({
        entityType: "problem_record",
        entityId: next.id,
        sourceReference: next.code,
        transformation: "In-application governed record update",
        dataQualityStatus: "verified",
      });

      return { ok: true };
    },
    [],
  );

  const setActiveProgram = useCallback((id: string | null) => {
    setActiveProgramId(id);
  }, []);

  /** Re-reads one record from the database so only confirmed state is shown. */
  const reloadRecord = useCallback(async (recordId: string) => {
    const { data: row } = await supabase
      .from("problem_records")
      .select("record")
      .eq("id", recordId)
      .maybeSingle();
    if (!row?.record) return;
    const rec = row.record as unknown as ProblemRecord;
    setData((prev) => ({
      ...prev,
      records: prev.records.map((r) => (r.id === recordId ? rec : r)),
    }));
    dataRef.current = {
      ...dataRef.current,
      records: dataRef.current.records.map((r) => (r.id === recordId ? rec : r)),
    };
  }, []);

  const adoptRecord = useCallback((record: ProblemRecord) => {
    setData((prev) =>
      prev.records.some((r) => r.id === record.id)
        ? prev
        : { ...prev, records: [...prev.records, record] },
    );
  }, []);

  const value = useMemo<WarrantyContextValue>(
    () => ({
      adoptRecord,
      data,
      suppliers,
      claims,
      hydrated,
      refreshRecord: reloadRecord,
      activeProgramId,
      setActiveProgram,
      // Class A — stage closure. Confirmed audit write before success.
      completeStage: (recordId, stageId) => {
        const rec = dataRef.current.records.find((r) => r.id === recordId);
        const st = rec?.stages.find((s) => s.id === stageId);
        if (!rec || !st) return Promise.resolve({ ok: false as const, error: NO_CHANGE });
        if (st.status === "complete")
          return Promise.resolve({ ok: false as const, error: NO_CHANGE });
        const earlier = rec.stages.find((s) => s.index < st.index && s.status !== "complete");
        if (earlier)
          return Promise.resolve({
            ok: false as const,
            error: "Complete Stage " + earlier.index + " (" + earlier.name + ") before this stage.",
          });
        const gate = stageGate(rec, st, eightDMapping);
        if (gate.blocked)
          return Promise.resolve({ ok: false as const, error: gate.message ?? NO_CHANGE });
        return commitRecord(
          recordId,
          (r) => {
            const stage = r.stages.find((s) => s.id === stageId);
            if (!stage || stageGate(r, stage, eightDMapping).blocked) return r;
            const stages = r.stages.map((s) =>
              s.id === stageId ? { ...s, status: "complete" as const, completedAt: nowIso() } : s,
            );
            const nextIdx = stages.findIndex((s) => s.status !== "complete");
            const withActive = stages.map((s, i) =>
              i === nextIdx && s.status === "not-started"
                ? { ...s, status: "in-progress" as const }
                : s,
            );
            return {
              ...r,
              stages: withActive,
              status: withActive.every((s) => s.status === "complete")
                ? ("closed" as const)
                : r.status,
              audit: [
                ...r.audit,
                {
                  id: `au-${uid()}`,
                  at: nowIso(),
                  actor: stage.owner,
                  action: `Stage ${stage.index} completed — ${stage.name}`,
                  clause: stage.clause.split(" — ")[0] ?? stage.clause,
                  detail: `Evidence gate satisfied with ${stage.evidence.length} required item(s).`,
                },
              ],
            };
          },
          async (_prev, next) => {
            const stage = next.stages.find((s) => s.id === stageId);
            if (!stage) throw new Error("stage missing");
            await recordAuditEvent({
              entityId: next.id,
              recordCode: next.code,
              stageId,
              eventType: "stage.completed",
              action: `Stage ${stage.index} completed — ${stage.name}`,
              clause: stage.clause,
              detail: `Evidence gate satisfied with ${stage.evidence.length} item(s).`,
              actorLabel: stage.owner,
              payload: {
                evidenceCount: stage.evidence.length,
                requiredEvidenceCount: stage.requiredEvidence.length,
              },
            });
          },
        );
      },
      // Class A — evidence behind a gate. Confirmed evidence + audit writes.
      addStageEvidence: async (recordId, stageId, name, attachment) => {
        const prepared = await prepareEvidence(name, attachment);
        if ("error" in prepared) return { ok: false, error: prepared.error };
        const item = prepared.item;
        return commitRecord(
          recordId,
          (r) => {
            const stage = r.stages.find((s) => s.id === stageId);
            if (!stage) return r;
            return {
              ...r,
              stages: r.stages.map((s) =>
                s.id === stageId
                  ? {
                      ...s,
                      status: s.status === "not-started" ? "in-progress" : s.status,
                      evidence: [...s.evidence, item],
                    }
                  : s,
              ),
              audit: [
                ...r.audit,
                {
                  id: `au-${uid()}`,
                  at: nowIso(),
                  actor: item.addedBy,
                  action: `Evidence attached — ${name}`,
                  clause: "ISO 9001 7.5.3",
                  detail: `${item.source === "file" ? `File ${item.fileName}` : `Reference ${item.reference}`} linked to stage ${stage.index} (${stage.name}).`,
                },
              ],
            };
          },
          async (_prev, next) => {
            const stage = next.stages.find((s) => s.id === stageId);
            if (!stage || !stage.evidence.some((e) => e.id === item.id))
              throw new Error("evidence missing");
            await registerEvidence({
              recordId: next.id,
              recordCode: next.code,
              stageId,
              chapter: "workflow",
              evidenceRef: item.id,
              name: item.name,
              evidenceType: item.source ?? item.type,
              sourceSystem:
                item.source === "file" ? "record-evidence-storage" : "external-reference",
              sourceReference:
                item.source === "file" ? (item.filePath ?? "") : (item.reference ?? ""),
              owner: item.addedBy,
              evidenceDate: item.addedAt.slice(0, 10),
              summary: item.summary,
            });
            try {
              await recordAuditEvent({
                entityId: next.id,
                recordCode: next.code,
                stageId,
                eventType: "evidence.attached",
                action: `Evidence attached — ${item.name}`,
                clause: "ISO 9001 7.5.3",
                detail: `${item.source === "file" ? `File ${item.fileName}` : `Reference ${item.reference}`} linked to stage ${stage.index} (${stage.name}).`,
                actorLabel: item.addedBy,
                payload: {
                  evidenceId: item.id,
                  source: item.source,
                  fileName: item.fileName ?? null,
                  filePath: item.filePath ?? null,
                  reference: item.reference ?? null,
                  description: item.description ?? null,
                  attachedBy: item.addedBy,
                  attachedByTitle: item.attachedByTitle ?? "",
                  attachedAt: item.addedAt,
                },
              });
            } catch (err) {
              await markEvidenceNotRecorded(next.id, item.id);
              throw err;
            }
          },
        );
      },
      // Class A — governance note on the record. Confirmed audit write.
      addStageNote: (recordId, stageId, text) =>
        commitRecord(
          recordId,
          (r) => ({
            ...r,
            stages: r.stages.map((s) =>
              s.id === stageId
                ? {
                    ...s,
                    notes: [...s.notes, { id: `n-${uid()}`, at: nowIso(), author: s.owner, text }],
                  }
                : s,
            ),
            audit: [
              ...r.audit,
              {
                id: `au-${uid()}`,
                at: nowIso(),
                actor: r.owner,
                action: "Note added to governed record",
                clause: "ISO 9001 7.5.3",
                detail: text.slice(0, 160),
              },
            ],
          }),
          async (_prev, next) => {
            await recordAuditEvent({
              entityId: next.id,
              recordCode: next.code,
              stageId,
              eventType: "note.added",
              action: "Note added to governed record",
              clause: "ISO 9001 7.5.3",
              detail: text.slice(0, 160),
              actorLabel: next.owner,
            });
          },
        ),
      // Class A — 8D chapter closure. Confirmed audit write.
      completeEightD: (recordId, dId) => {
        const rec = dataRef.current.records.find((r) => r.id === recordId);
        const dStep = rec?.eightD.find((x) => x.id === dId);
        if (!rec || !dStep) return Promise.resolve({ ok: false as const, error: NO_CHANGE });
        const gate = dGate(rec, dStep, eightDMapping);
        if (gate.blocked)
          return Promise.resolve({ ok: false as const, error: gate.message ?? NO_CHANGE });
        return commitRecord(
          recordId,
          (r) => {
            const d = r.eightD.find((x) => x.id === dId);
            if (!d || dGate(r, d, eightDMapping).blocked) return r;
            const linked = stageForD(r, dId, eightDMapping);
            return {
              ...r,
              stages: r.stages.map((s) =>
                s.id === linked?.id && s.status === "not-started"
                  ? { ...s, status: "in-progress" as const }
                  : s,
              ),
              eightD: r.eightD.map((x) =>
                x.id === dId ? { ...x, status: "complete" as const } : x,
              ),
              audit: [
                ...r.audit,
                {
                  id: `au-${uid()}`,
                  at: nowIso(),
                  actor: d.owner,
                  action: `${dId} completed — ${d.title}`,
                  clause: "IATF 16949 10.2.3",
                  detail: "8D chapter step closed with required deliverables attached.",
                },
              ],
            };
          },
          async (_prev, next) => {
            const d = next.eightD.find((x) => x.id === dId);
            if (!d) throw new Error("chapter missing");
            await recordAuditEvent({
              entityId: next.id,
              recordCode: next.code,
              eventType: "eightd.completed",
              action: `${dId} completed — ${d.title}`,
              clause: "IATF 16949 10.2.3",
              detail: "8D chapter step closed with required deliverables attached.",
              actorLabel: d.owner,
              payload: { chapter: dId },
            });
          },
        );
      },
      // Class A — evidence behind an 8D gate. Confirmed evidence + audit writes.
      addEightDEvidence: async (recordId, dId, name, attachment) => {
        const prepared = await prepareEvidence(name, attachment);
        if ("error" in prepared) return { ok: false, error: prepared.error };
        const item = prepared.item;
        return commitRecord(
          recordId,
          (r) => {
            const d = r.eightD.find((x) => x.id === dId);
            if (!d) return r;
            const linked = stageForD(r, dId, eightDMapping);
            return {
              ...r,
              stages: r.stages.map((s) =>
                s.id === linked?.id && s.status === "not-started"
                  ? { ...s, status: "in-progress" as const }
                  : s,
              ),
              eightD: r.eightD.map((x) =>
                x.id === dId
                  ? {
                      ...x,
                      status: x.status === "not-started" ? "in-progress" : x.status,
                      evidence: [...x.evidence, item],
                    }
                  : x,
              ),
              audit: [
                ...r.audit,
                {
                  id: `au-${uid()}`,
                  at: nowIso(),
                  actor: item.addedBy,
                  action: `Evidence attached — ${name}`,
                  clause: "ISO 9001 7.5.3",
                  detail: `Deliverable linked to ${dId}.`,
                },
              ],
            };
          },
          async (_prev, next) => {
            const d = next.eightD.find((x) => x.id === dId);
            if (!d || !d.evidence.some((e) => e.id === item.id))
              throw new Error("evidence missing");
            await registerEvidence({
              recordId: next.id,
              recordCode: next.code,
              stageId: dId,
              chapter: "8d",
              evidenceRef: item.id,
              name: item.name,
              evidenceType: item.source ?? item.type,
              sourceSystem:
                item.source === "file" ? "record-evidence-storage" : "external-reference",
              sourceReference:
                item.source === "file" ? (item.filePath ?? "") : (item.reference ?? ""),
              owner: item.addedBy,
              evidenceDate: item.addedAt.slice(0, 10),
              summary: item.summary,
            });
            try {
              await recordAuditEvent({
                entityId: next.id,
                recordCode: next.code,
                eventType: "evidence.attached",
                action: `Evidence attached — ${item.name}`,
                clause: "ISO 9001 7.5.3",
                detail: `Deliverable linked to ${dId}.`,
                actorLabel: item.addedBy,
                payload: {
                  chapter: dId,
                  evidenceId: item.id,
                  source: item.source,
                  fileName: item.fileName ?? null,
                  filePath: item.filePath ?? null,
                  reference: item.reference ?? null,
                  description: item.description ?? null,
                  attachedBy: item.addedBy,
                  attachedByTitle: item.attachedByTitle ?? "",
                  attachedAt: item.addedAt,
                },
              });
            } catch (err) {
              await markEvidenceNotRecorded(next.id, item.id);
              throw err;
            }
          },
        );
      },
      // Class A — PFMEA dual sign-off (Package #2.2). Each step is one
      // transactional database call (authority → governed write → audit); the
      // record is then re-read so the page shows only confirmed state.
      proposePfmeaChange: async (recordId, rowId, field, newValue, rationale, openRequest) => {
        const record = dataRef.current.records.find((r) => r.id === recordId);
        if (!record) return { ok: false, error: NO_CHANGE };
        let auth: { roles: AppRole[]; rules: AuthorityRule[] };
        try {
          auth = await loadAuthority();
        } catch {
          return { ok: false, error: "Authority could not be verified. Nothing was recorded." };
        }
        const result = await submitPfmeaProposal(rpc, {
          record,
          row: record.pfmea.find((x) => x.id === rowId),
          field,
          newValue,
          rationale,
          openRequest,
          ...auth,
        });
        await reloadRecord(recordId);
        return result;
      },
      signOffPfmea: async (recordId, request, signoffs, decision, role, comment) => {
        const record = dataRef.current.records.find((r) => r.id === recordId);
        if (!record) return { ok: false, error: NO_CHANGE };
        const { data: u } = await supabase.auth.getUser();
        let auth: { roles: AppRole[]; rules: AuthorityRule[] };
        try {
          auth = await loadAuthority();
        } catch {
          return { ok: false, error: "Authority could not be verified. Nothing was recorded." };
        }
        const result = await submitPfmeaSignoff(rpc, {
          record,
          request,
          signoffs,
          userId: u.user?.id ?? null,
          decision,
          role,
          comment,
          ...auth,
        });
        await reloadRecord(recordId);
        return result;
      },
      changeClauseVerdict: async (recordId, clauseId, newVerdict, rationale, evidence) => {
        const record = dataRef.current.records.find((r) => r.id === recordId);
        if (!record) return { ok: false, error: NO_CHANGE };
        let auth: { roles: AppRole[]; rules: AuthorityRule[] };
        try {
          auth = await loadAuthority();
        } catch {
          return { ok: false, error: "Authority could not be verified. Nothing was recorded." };
        }
        const result = await submitVerdictChange(rpc, {
          record,
          clauseId,
          newVerdict,
          rationale,
          evidence,
          ...auth,
        });
        await reloadRecord(recordId);
        return result.ok ? { ok: true } : result;
      },
      changeRecordCarryFactor: async (recordId, newValue, rationale) => {
        const record = dataRef.current.records.find((r) => r.id === recordId);
        if (!record) return { ok: false, error: NO_CHANGE };
        let auth: { roles: AppRole[]; rules: AuthorityRule[] };
        try {
          auth = await loadAuthority();
        } catch {
          return { ok: false, error: "Authority could not be verified. Nothing was recorded." };
        }
        const result = await submitRecordCarryFactor(carryRpc, {
          record,
          newValue,
          rationale,
          ...auth,
        });
        await reloadRecord(recordId);
        return result;
      },
      changeDefaultCarryFactor: async (newValue, rationale) => {
        let auth: { roles: AppRole[]; rules: AuthorityRule[] };
        try {
          auth = await loadAuthority();
        } catch {
          return { ok: false, error: "Authority could not be verified. Nothing was recorded." };
        }
        const result = await submitDefaultCarryFactor(carryRpc, {
          currentValue: defaultCarryFactor,
          newValue,
          rationale,
          ...auth,
        });
        if (result.ok) setDefaultCarryFactor(newValue);
        return result;
      },
      defaultCarryFactor,
      // Class A — authorised human decision. Confirmed audit event AND
      // Evidence Snapshot at Decision before the decision counts as recorded.
      decideRecommendation: (recordId, rec, verdict, finalAction, note) => {
        const label =
          verdict === "accepted"
            ? "accepted"
            : verdict === "modified"
              ? "accepted with modification"
              : "rejected";
        return commitRecord(
          recordId,
          (r) => {
            const stage = r.stages.find((s) => s.id === rec.stageId);
            const actor = stage?.owner ?? r.owner;
            return {
              ...r,
              decisions: [
                ...(r.decisions ?? []).filter((d) => d.recommendationId !== rec.id),
                {
                  id: `dec-${uid()}`,
                  recommendationId: rec.id,
                  stageId: rec.stageId,
                  verdict,
                  finalAction,
                  note,
                  decidedBy: actor,
                  at: nowIso(),
                },
              ],
              audit: [
                ...r.audit,
                {
                  id: `au-${uid()}`,
                  at: nowIso(),
                  actor,
                  action: `Recommendation ${label} — ${rec.title}`,
                  clause: rec.clause,
                  detail: `${finalAction}${note ? ` · reviewer: ${note}` : ""}`,
                },
              ],
            };
          },
          async (_prev, next) => {
            const stage = next.stages.find((s) => s.id === rec.stageId);
            const decision = (next.decisions ?? []).find((d) => d.recommendationId === rec.id);
            const auditEventId = await recordAuditEvent({
              entityId: next.id,
              recordCode: next.code,
              stageId: rec.stageId,
              eventType: "recommendation.decided",
              action: `Recommendation ${label} — ${rec.title}`,
              clause: rec.clause,
              detail: `${finalAction}${note ? ` · reviewer: ${note}` : ""}`,
              actorLabel: decision?.decidedBy ?? next.owner,
              decisionVerdict: verdict,
              recommendationId: rec.id,
              payload: {
                // AI drafting is advisory only; this is the authorised human decision.
                authorisedBy: "human",
                confidence: rec.confidence,
                urgency: rec.urgency,
              },
            });
            await captureEvidenceSnapshot({
              record: next,
              stage,
              decisionId: decision?.id ?? "",
              recommendationId: rec.id,
              verdict,
              auditEventId,
            });
          },
        );
      },
      eightDMapping,
      // Class A — 8D due date. Blocked past the linked stage's due date.
      setEightDDueDate: async (recordId, dId, date) => {
        const rec = dataRef.current.records.find((r) => r.id === recordId);
        if (!rec) return { ok: false, error: NO_CHANGE };
        const err = validateDDueDate(rec, dId, date, eightDMapping);
        if (err) return { ok: false, error: err };
        const old = rec.eightD.find((x) => x.id === dId)?.dueDate ?? "";
        return commitRecord(
          recordId,
          (r) => ({
            ...r,
            eightD: r.eightD.map((x) => (x.id === dId ? { ...x, dueDate: date } : x)),
            audit: [
              ...r.audit,
              {
                id: `au-${uid()}`,
                at: nowIso(),
                actor: r.owner,
                action: `${dId} due date changed`,
                clause: "IATF 16949 10.2.3",
                detail: `${old.slice(0, 10)} → ${date}`,
              },
            ],
          }),
          async (_prev, next) => {
            await recordAuditEvent({
              entityId: next.id,
              recordCode: next.code,
              eventType: "eightd.due_date_changed",
              action: `${dId} due date changed`,
              clause: "IATF 16949 10.2.3",
              detail: `${old.slice(0, 10)} → ${date}`,
              actorLabel: next.owner,
              payload: { chapter: dId, oldValue: old, newValue: date },
            });
          },
        );
      },
    }),
    [
      data,
      suppliers,
      claims,
      hydrated,
      activeProgramId,
      commitRecord,
      setActiveProgram,
      adoptRecord,
      reloadRecord,
      eightDMapping,
      defaultCarryFactor,
    ],
  );

  return (
    <WarrantyContext.Provider value={value}>
      {!hydrated ? (
        <p role="status" className="p-6 text-sm">
          Loading the synthetic workflow snapshot…
        </p>
      ) : loadError ? (
        <p role="alert" className="p-6 text-sm text-destructive">
          The synthetic workflow snapshot is unavailable. Return to the portal and reopen the demo.
        </p>
      ) : (
        children
      )}
    </WarrantyContext.Provider>
  );
}

export function useWarranty() {
  const ctx = useContext(WarrantyContext);
  if (!ctx) throw new Error("useWarranty must be used inside WarrantyProvider");
  return ctx;
}

export function useRecord(id: string) {
  const { data } = useWarranty();
  return data.records.find((r) => r.id === id || r.code.toLowerCase() === id.toLowerCase());
}

export function useClaims(recordId: string | null | undefined) {
  const { claims } = useWarranty();
  if (!recordId) return [];
  return claims.filter((c) => c.recordId === recordId);
}

export function useSupplier(name: string | null | undefined) {
  const { suppliers } = useWarranty();
  if (!name) return undefined;
  return suppliers.find((s) => s.name.toLowerCase() === name.toLowerCase());
}

export function useProgram(id: string | null | undefined) {
  const { data } = useWarranty();
  return data.programs.find((p) => p.id === id);
}
